import { writable, derived } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';

// Version control store for managing document versions
function createVersionStore() {
    const { subscribe, set, update } = writable({
        versions: [], // Array of saved versions
        currentVersion: null, // Currently loaded version
        hasUnsavedChanges: false,
        lastSavedContent: { spec: '', claims: '' }, // Content of the last saved/loaded version
    });

    return {
        subscribe,
        
        // Save a new version (in-memory only, not persisted)
        saveVersion: (name, specContent, claimsContent) => {
            const newVersion = {
                id: uuidv4(),
                name,
                createdAt: new Date().toISOString(),
                content: {
                    spec: specContent,
                    claims: claimsContent
                }
            };

            update(state => ({
                ...state,
                versions: [newVersion, ...state.versions],
                currentVersion: newVersion,
                hasUnsavedChanges: false,
                lastSavedContent: { spec: specContent, claims: claimsContent }
            }));

            return newVersion;
        },
        
        // Load a version (from in-memory store only)
        loadVersion: (versionId) => {
            let foundVersion = null;
            update(state => {
                const version = state.versions.find(v => v.id === versionId);
                if (version) {
                    foundVersion = version;
                    return {
                        ...state,
                        currentVersion: version,
                        hasUnsavedChanges: false,
                        lastSavedContent: {
                            spec: version.content.spec,
                            claims: version.content.claims
                        }
                    };
                }
                return state;
            });
            return foundVersion;
        },
        
        // Delete a version (from in-memory store only)
        deleteVersion: (versionId) => {
            update(state => {
                const newVersions = state.versions.filter(v => v.id !== versionId);
                const newState = {
                    ...state,
                    versions: newVersions
                };

                // If we deleted the current version, clear it
                if (state.currentVersion?.id === versionId) {
                    newState.currentVersion = null;
                }

                return newState;
            });
        },
        
        // Mark that content has changed
        markAsChanged: () => update(state => ({ ...state, hasUnsavedChanges: true })),
        
        // Check if content has changed compared to last saved version
        checkForChanges: (currentSpec, currentClaims) => {
            update(state => {
                const hasChanges = 
                    currentSpec !== state.lastSavedContent.spec || 
                    currentClaims !== state.lastSavedContent.claims;
                return { ...state, hasUnsavedChanges: hasChanges };
            });
        },
        
        // Get all versions (from in-memory store only)
        getVersions: () => {
            let versions = [];
            subscribe(state => {
                versions = state.versions;
            })();
            return versions;
        },

        // Load versions from localStorage (used when importing .pdft files)
        loadFromStorage: () => {
            const storedVersions = localStorage.getItem('patdraft_versions');
            if (storedVersions) {
                const versions = JSON.parse(storedVersions);
                update(state => ({ ...state, versions }));
            }
        },

        // Clear current version (when starting fresh)
        clearCurrentVersion: () => update(state => ({
            ...state,
            currentVersion: null,
            hasUnsavedChanges: false,
            lastSavedContent: { spec: '', claims: '' }
        })),
        
        reset: () => set({
            versions: [],
            currentVersion: null,
            hasUnsavedChanges: false,
            lastSavedContent: { spec: '', claims: '' }
        })
    };
}

export const versionStore = createVersionStore();

// Derived store for version count
export const versionCount = derived(
    versionStore,
    $versionStore => $versionStore.versions.length
);